import { create } from "zustand";

const COLLAPSE_STORAGE_KEY = "ftl_sidebar_collapsed";

function loadCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function persistCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed));
  } catch {
    /* ignore */
  }
}

interface SidebarState {
  /** Which nav section is active (null = panel closed, ribbon only) */
  activeSection: string | null;
  /** Whether Felix occupies the panel area */
  felixMode: boolean;
  /** Mobile drawer open */
  mobileOpen: boolean;
  /** True = icon-only rail view (72px). False = full sidebar (304px). */
  collapsed: boolean;
  setActiveSection: (section: string | null) => void;
  toggleSection: (section: string) => void;
  setFelixMode: (mode: boolean) => void;
  /** Compat: toggles panel open/closed */
  toggle: () => void;
  setCollapsed: (collapsed: boolean) => void;
  /** Flip between rail and full sidebar view. */
  toggleCollapsed: () => void;
  setMobileOpen: (open: boolean) => void;
}

export const useSidebarStore = create<SidebarState>((set) => ({
  activeSection: "MAIN",
  felixMode: false,
  mobileOpen: false,
  collapsed: loadCollapsed(),
  setActiveSection: (section) => set({ activeSection: section }),
  toggleSection: (section) =>
    set((s) => {
      const next = s.activeSection === section ? null : section;
      return { activeSection: next };
    }),
  setFelixMode: (felixMode) => set({ felixMode }),
  toggle: () =>
    set((s) => {
      if (s.felixMode) return { felixMode: false };
      const next = s.activeSection === null ? "MAIN" : null;
      return { activeSection: next };
    }),
  setCollapsed: (collapsed) => {
    persistCollapsed(collapsed);
    set({ collapsed });
  },
  toggleCollapsed: () =>
    set((s) => {
      const next = !s.collapsed;
      persistCollapsed(next);
      return { collapsed: next };
    }),
  setMobileOpen: (mobileOpen) => set({ mobileOpen }),
}));
